import React, {FormEvent} from 'react';
import HomeModalShell from '../../home/components/HomeModalShell';
import {SafetyBlockedUser} from '../../../../services/safetyApi';
import {getAssetUrl, getDisplayInitial} from '../../../../utils/assets';
import styles from '../Profile.module.css';

interface ProfileSafetySettingsModalProps {
  isOpen: boolean;
  blockedWordsText: string;
  blockObscenity: boolean;
  locationSharingEnabled: boolean;
  blockedUsers: SafetyBlockedUser[];
  loading: boolean;
  saving: boolean;
  unblockingUserId: string | null;
  error: string | null;
  validationError: string | null;
  success: string | null;
  onBlockedWordsChange: (value: string) => void;
  onBlockObscenityChange: (value: boolean) => void;
  onLocationSharingChange: (value: boolean) => void;
  onUnblockUser: (userId: string) => void;
  onClose: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}

const ProfileSafetySettingsModal: React.FC<ProfileSafetySettingsModalProps> = ({
  isOpen,
  blockedWordsText,
  blockObscenity,
  locationSharingEnabled,
  blockedUsers,
  loading,
  saving,
  unblockingUserId,
  error,
  validationError,
  success,
  onBlockedWordsChange,
  onBlockObscenityChange,
  onLocationSharingChange,
  onUnblockUser,
  onClose,
  onSubmit,
}) => {
  if (!isOpen) {
    return null;
  }

  const controlsDisabled = loading || saving;

  return (
    <HomeModalShell
      backdropClassName={styles.editorModalBackdrop}
      panelClassName={`${styles.socialModalCard} ${styles.safetyModalCard}`}
      ariaLabelledBy="profile-safety-settings-title"
      ariaDescribedBy="profile-safety-settings-description"
      onClose={onClose}
      closeDisabled={controlsDisabled || Boolean(unblockingUserId)}
    >
      <div className={styles.editorModalHeader}>
        <div>
          <h2 id="profile-safety-settings-title" className={styles.editorModalTitle}>
            Safety Features
          </h2>
          <p id="profile-safety-settings-description" className={styles.editorModalDescription}>
            Manage guardrails, blocked users, and location sharing for your profile.
          </p>
        </div>
        <button
          type="button"
          className={styles.editorModalClose}
          onClick={onClose}
          disabled={controlsDisabled || Boolean(unblockingUserId)}
          aria-label="Close safety settings"
        >
          x
        </button>
      </div>

      {loading ? (
        <div className={styles.safetyEmptyState}>Loading safety settings...</div>
      ) : (
        <form onSubmit={onSubmit} className={styles.editorModalForm}>
          <div className={styles.safetyToggleList}>
            <label className={styles.safetyToggleRow}>
              <span>
                <strong>Block common obscenity</strong>
                <small>Reject posts, comments, and messages that match the server guardrail list.</small>
              </span>
              <input
                type="checkbox"
                checked={blockObscenity}
                onChange={(event) => onBlockObscenityChange(event.target.checked)}
                disabled={controlsDisabled}
              />
            </label>

            <label className={styles.safetyToggleRow}>
              <span>
                <strong>Share profile location</strong>
                <small>Show your location in public profile, talent search, and service results.</small>
              </span>
              <input
                type="checkbox"
                checked={locationSharingEnabled}
                onChange={(event) => onLocationSharingChange(event.target.checked)}
                disabled={controlsDisabled}
              />
            </label>
          </div>

          <label className={styles.editorField}>
            <span className={styles.editorFieldLabel}>Blocked words or phrases</span>
            <textarea
              className={`${styles.editorInput} ${styles.safetyTextarea}`}
              value={blockedWordsText}
              onChange={(event) => onBlockedWordsChange(event.target.value)}
              placeholder="One word or phrase per line"
              disabled={controlsDisabled}
            />
            <span className={styles.editorHint}>
              These terms are normalized before saving and enforced without showing the matched term to other users.
            </span>
          </label>

          {validationError ? (
            <div className={`${styles.editorMessage} ${styles.editorMessageValidation}`}>
              {validationError}
            </div>
          ) : null}

          {error ? (
            <div className={`${styles.editorMessage} ${styles.editorMessageError}`}>
              {error}
            </div>
          ) : null}

          {success ? (
            <div className={`${styles.editorMessage} ${styles.editorMessageSuccess}`}>
              {success}
            </div>
          ) : null}

          <section className={styles.safetyBlockedUsersSection}>
            <h3 className={styles.safetySubheading}>Blocked Users</h3>
            {blockedUsers.length > 0 ? (
              <div className={styles.safetyBlockedUsersList}>
                {blockedUsers.map((blockedUser) => {
                  const avatarUrl = getAssetUrl(blockedUser.profile?.profilePicture);
                  const displayName = blockedUser.profile?.name || 'Blocked user';
                  const isUnblocking = unblockingUserId === blockedUser._id;

                  return (
                    <div key={blockedUser._id} className={styles.safetyBlockedUser}>
                      <div className={styles.safetyBlockedUserIdentity}>
                        {avatarUrl ? (
                          <img src={avatarUrl} alt="" className={styles.safetyBlockedUserAvatar} />
                        ) : (
                          <div className={styles.safetyBlockedUserAvatarFallback}>
                            {getDisplayInitial(displayName)}
                          </div>
                        )}
                        <span>{displayName}</span>
                      </div>

                      <button
                        type="button"
                        className={`${styles.editorModalButton} ${styles.editorModalButtonSecondary}`}
                        onClick={() => onUnblockUser(blockedUser._id)}
                        disabled={controlsDisabled || Boolean(unblockingUserId)}
                      >
                        {isUnblocking ? 'Unblocking...' : 'Unblock'}
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className={styles.safetyEmptyState}>No users are blocked.</div>
            )}
          </section>

          <div className={styles.editorModalActions}>
            <button
              type="button"
              className={`${styles.editorModalButton} ${styles.editorModalButtonSecondary}`}
              onClick={onClose}
              disabled={controlsDisabled || Boolean(unblockingUserId)}
            >
              Close
            </button>
            <button
              type="submit"
              className={`${styles.editorModalButton} ${styles.editorModalButtonPrimary}`}
              disabled={controlsDisabled || Boolean(unblockingUserId)}
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </form>
      )}
    </HomeModalShell>
  );
};

export default React.memo(ProfileSafetySettingsModal);
